import {
  DeferredCommitStatusGlyph,
  deferredCommitStatusGlyphVisible,
} from "@/components/mrp/DeferredCommitStatusGlyph";
import {
  deferredCommitControlClassName,
  useDeferredCommitFieldFeedback,
} from "@/components/mrp/deferredCommitFieldShared";
import { FreeTextSuggestionInput } from "@/components/ui/FreeTextSuggestionInput";
import { Label } from "@/components/ui/Input";
import { cn } from "@/lib/utils";
import type { WarehouseRecountShelfSublocation } from "@/lib/warehouseRecountApi";

const EMPTY_SHELF_LABEL = "No sub location";

function normalizeShelfDraft(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed.toLowerCase() === EMPTY_SHELF_LABEL.toLowerCase()) {
    return "";
  }
  return trimmed.toUpperCase();
}

export function warehouseRecountShelfDraftToStorageKey(draft: string): string {
  return normalizeShelfDraft(draft);
}

export function warehouseRecountShelfStorageKeyToDraft(storageKey: string): string {
  const trimmed = storageKey.trim();
  return trimmed === "" ? "" : trimmed;
}

/** A tap that leaves the shelf field also clicks the shelf row under the finger. */
const SHELF_FIELD_BLUR_OWNS_TAP_MS = 500;

let shelfFieldBlurCommit: { atMs: number; draft: string } | null = null;

export function noteWarehouseRecountShelfFieldBlur(draft: string, nowMs: number = Date.now()): void {
  shelfFieldBlurCommit = { atMs: nowMs, draft };
}

/** True when this row tap is the same gesture that left the field with a different shelf. */
export function shelfRowTapRepeatsFieldBlur(
  rowDraft: string,
  nowMs: number = Date.now(),
): boolean {
  const recent = shelfFieldBlurCommit;
  if (recent == null) return false;
  if (nowMs - recent.atMs > SHELF_FIELD_BLUR_OWNS_TAP_MS) return false;
  return normalizeShelfDraft(recent.draft) !== normalizeShelfDraft(rowDraft);
}

export function WarehouseRecountShelfList(props: {
  shelfDraft: string;
  shelfSublocations: readonly WarehouseRecountShelfSublocation[];
  disabled?: boolean;
  onShelfDraftChange: (draft: string) => void;
  /** When set, picking a row uses the same commit as leaving the shelf field. */
  onCommit?: (draft: string) => void;
  referenceTableTitle?: string | null;
}): JSX.Element | null {
  if (props.shelfSublocations.length === 0) {
    return (
      <p className="text-sm text-slate-600">
        No cartons are on a shelf at this warehouse.
      </p>
    );
  }

  const showingLastShelf = props.shelfSublocations.every((row) => row.last_known === true);

  return (
    <div className="space-y-2">
      {props.referenceTableTitle != null && props.referenceTableTitle.trim() !== "" ? (
        <h3 className="text-sm font-semibold text-slate-900">{props.referenceTableTitle}</h3>
      ) : null}
      {showingLastShelf ? (
        <p className="text-sm text-slate-600">Last sublocation. No cartons are left on a shelf.</p>
      ) : null}
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2 font-medium">Shelf</th>
              <th className="px-3 py-2 font-medium text-right">Labeled qty</th>
              <th className="px-3 py-2 font-medium text-right">Containers</th>
            </tr>
          </thead>
          <tbody>
            {props.shelfSublocations.map((row) => {
              const selected =
                normalizeShelfDraft(props.shelfDraft) === normalizeShelfDraft(row.sublocation);
              return (
                <tr
                  key={row.sublocation || "__empty__"}
                  className={cn(
                    "border-t border-slate-200",
                    selected ? "bg-brand-50" : "bg-white",
                    props.disabled ? "opacity-60" : "cursor-pointer hover:bg-slate-50",
                  )}
                  onClick={() => {
                    if (props.disabled) return;
                    const next = row.sublocation === "" ? "" : normalizeShelfDraft(row.label);
                    if (shelfRowTapRepeatsFieldBlur(next)) return;
                    props.onShelfDraftChange(next);
                    props.onCommit?.(next);
                  }}
                >
                  <td className="px-3 py-2 font-medium text-slate-900">{row.label}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {(row.labeled_quantity ?? 0).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.carton_count.toLocaleString()}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function WarehouseRecountShelfField(props: {
  id: string;
  shelfDraft: string;
  shelfSublocations: readonly WarehouseRecountShelfSublocation[];
  disabled?: boolean;
  onShelfDraftChange: (draft: string) => void;
  /** When set, shows the shelf table above the input (putaway reference). */
  referenceTableTitle?: string | null;
  referenceTableFirst?: boolean;
  /** When false, the caller renders WarehouseRecountShelfList separately. */
  showList?: boolean;
  inputClassName?: string;
  onCommit?: (draft: string) => void;
  /** Saved shelf key. When set with onCommit, the field shows unsaved, saving, and saved. */
  savedShelf?: string;
  commitPending?: boolean;
  commitSucceeded?: boolean;
  commitError?: string | null;
}): JSX.Element {
  const referenceTableFirst = props.referenceTableFirst === true;
  const shelfSuggestions = props.shelfSublocations.map((row) => row.label);
  const showCommitStatus = props.onCommit != null && props.savedShelf != null;
  const isDirty =
    showCommitStatus &&
    warehouseRecountShelfDraftToStorageKey(props.shelfDraft) !==
      warehouseRecountShelfDraftToStorageKey(props.savedShelf ?? "");
  const flashTone = useDeferredCommitFieldFeedback(
    showCommitStatus ? props.commitSucceeded : false,
    showCommitStatus && !isDirty ? props.commitError : null,
    { feedbackId: props.id },
  );
  const showGlyph =
    showCommitStatus &&
    deferredCommitStatusGlyphVisible({
      pending: props.commitPending,
      isDirty,
      commitError: props.commitError,
    });

  const table = props.showList === false ? null : (
    <WarehouseRecountShelfList
      shelfDraft={props.shelfDraft}
      shelfSublocations={props.shelfSublocations}
      disabled={props.disabled}
      onShelfDraftChange={props.onShelfDraftChange}
      onCommit={props.onCommit}
      referenceTableTitle={props.referenceTableTitle}
    />
  );

  const input = (
    <div>
      <Label htmlFor={props.id}>Shelf sublocation</Label>
      <div className="relative mt-1">
      <FreeTextSuggestionInput
        id={props.id}
        inputClassName={cn(
          props.inputClassName ?? "h-10 font-mono text-sm",
          showCommitStatus &&
            deferredCommitControlClassName({
              isDirty,
              disabled: props.disabled === true,
              pending: props.commitPending,
              commitError: props.commitError,
              flashTone,
            }),
        )}
        placeholder="Shelf label, for example A1-02"
        value={props.shelfDraft}
        disabled={props.disabled}
        suggestions={shelfSuggestions}
        onChange={(draft) => props.onShelfDraftChange(draft.toUpperCase())}
        onCommit={(draft) => {
          noteWarehouseRecountShelfFieldBlur(draft);
          props.onCommit?.(draft);
        }}
      />
      {showGlyph ? (
        <div className="pointer-events-none absolute right-1 top-1.5 z-10">
          <div className="pointer-events-auto">
            <DeferredCommitStatusGlyph
              pending={props.commitPending}
              isDirty={isDirty}
              commitError={props.commitError}
            />
          </div>
        </div>
      ) : null}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {referenceTableFirst ? table : null}
      {input}
      {!referenceTableFirst ? table : null}
    </div>
  );
}
